import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { surveyAPI, operationAPI } from '../services/api'
import SurveyReportsModal from '../components/SurveyReportsModal'

const SurveyList = () => {
  const { isSuperuser, userGroups, isFinanceManager, isManager } = useAuth()
  // Direct group-based and user-based access control
  const isAdmin = isSuperuser || userGroups.includes('Admin')
  const hasFullAccess = isSuperuser || isAdmin || isFinanceManager
  const navigate = useNavigate()
  const [surveys, setSurveys] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchCategory, setSearchCategory] = useState('all')
  const [showCalendarOnly, setShowCalendarOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [calendarLoading, setCalendarLoading] = useState({})
  const [expandedCards, setExpandedCards] = useState(new Set())
  const [showFilterFrame, setShowFilterFrame] = useState(false)
  const [reportsModalRef, setReportsModalRef] = useState(null)

  useEffect(() => {
    // Only Manager, Finance Manager, Admin, or Superuser can access surveys
    const allowedGroups = ['Manager', 'Finance Manager', 'Admin']
    const hasAccess = isSuperuser || userGroups.some(group => allowedGroups.includes(group))
    
    if (!hasAccess) {
      navigate('/operations')
      return
    }
    fetchSurveys()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperuser, userGroups, page, searchQuery, searchCategory, showCalendarOnly])

  const fetchSurveys = async () => {
    setLoading(true)
    try {
      const params = {
        page,
        search: searchQuery,
        category: searchCategory,
      }
      if (showCalendarOnly) {
        params.calendar = 'true'
      }
      const response = await surveyAPI.list(params)
      setSurveys(response.data.results)
      setTotalPages(response.data.total_pages)
    } catch (error) {
      console.error('Error fetching surveys:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleCard = (referenceNumber, e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setExpandedCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(referenceNumber)) {
        newSet.delete(referenceNumber)
      } else {
        newSet.add(referenceNumber)
      }
      return newSet
    })
  }

  const handleToggleStatus = async (id, e) => {
    e?.stopPropagation()
    const currentSurvey = surveys.find(s => s.reference_number === id)
    
    if (!currentSurvey) {
      return
    }
    try {
      await surveyAPI.toggleStatus(id)
      
      await fetchSurveys()
    } catch (error) {
      console.error('Error toggling status:', error)
      alert(error.response?.data?.message || 'Failed to toggle status')
    }
  }

  const handleToggleInvoiceSent = async (referenceNumber, invoiceSent, e) => {
    e?.stopPropagation()
    if (!window.confirm(`Are you sure you want to mark this invoice as ${invoiceSent ? 'sent' : 'not sent'}?`)) {
      return
    }
    try {
      const response = await surveyAPI.toggleInvoiceSent(referenceNumber, invoiceSent)
      if (response.data?.success) {
        await fetchSurveys()
      } else {
        alert(response.data?.message || 'Failed to update invoice status')
      }
    } catch (error) {
      console.error('Error toggling invoice sent:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Failed to update invoice status'
      alert(errorMessage)
    }
  }

  const handleAddToCalendar = async (referenceNumber, e) => {
    e?.stopPropagation()
    setCalendarLoading(prev => ({ ...prev, [referenceNumber]: true }))
    try {
      await operationAPI.createFromSurvey(referenceNumber)
      await fetchSurveys()
    } catch (error) {
      console.error('Error adding to calendar:', error)
      alert(error.response?.data?.message || 'Failed to add survey to calendar')
    } finally {
      setCalendarLoading(prev => ({ ...prev, [referenceNumber]: false }))
    }
  }

  const handleRemoveFromCalendar = async (referenceNumber, e) => {
    e?.stopPropagation()
    setCalendarLoading(prev => ({ ...prev, [referenceNumber]: true }))
    try {
      await operationAPI.deleteByRef(referenceNumber)
      await fetchSurveys()
    } catch (error) {
      console.error('Error removing from calendar:', error)
      alert(error.response?.data?.message || 'Failed to remove survey from calendar')
    } finally {
      setCalendarLoading(prev => ({ ...prev, [referenceNumber]: false }))
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    
    // Parse date string as date components to avoid timezone conversion issues
    // Backend sends dates as YYYY-MM-DD strings which should be timezone-neutral
    let year, month, day
    
    if (typeof dateString === 'string') {
      // Extract YYYY-MM-DD from string (handle both pure date strings and ISO datetime strings)
      const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (dateMatch) {
        year = parseInt(dateMatch[1], 10)
        month = parseInt(dateMatch[2], 10) - 1 // Month is 0-indexed
        day = parseInt(dateMatch[3], 10)
      } else {
        // Fallback: parse as Date and extract components in Europe/London timezone
        const date = new Date(dateString)
        const formatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Europe/London',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        const parts = formatter.format(date).split('-')
        year = parseInt(parts[0], 10)
        month = parseInt(parts[1], 10) - 1
        day = parseInt(parts[2], 10)
      }
    } else {
      // If it's already a Date object, extract components in Europe/London timezone
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      const parts = formatter.format(dateString).split('-')
      year = parseInt(parts[0], 10)
      month = parseInt(parts[1], 10) - 1
      day = parseInt(parts[2], 10)
    }
    
    // Create a date object with the parsed components and format it
    // Using local date constructor avoids timezone conversion
    const date = new Date(year, month, day)
    return date.toLocaleDateString('en-GB', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    })
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-'
    return `$${parseFloat(value).toFixed(2)}`
  }

  const formatFloat = (value, decimals = 3) => {
    if (value === null || value === undefined) return '-'
    return parseFloat(value).toFixed(decimals)
  }

  const allowedGroups = ['Manager', 'Finance Manager', 'Admin']
  const hasAccess = isSuperuser || userGroups.some(group => allowedGroups.includes(group))
  
  if (!hasAccess) return null

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center justify-between w-full sm:w-auto">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold"><span className="text-premium">JNJ Surveys</span></h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">Manage and track survey operations</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={() => setShowFilterFrame(!showFilterFrame)}
                className="p-2 rounded-lg bg-jnj-neon-500 hover:bg-jnj-neon-600 text-white transition-all duration-200 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
                title="Toggle Filters"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </button>
              <button
                onClick={() => navigate('/surveys/new')}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-jnj-neon-500 to-jnj-neon-600 hover:from-jnj-neon-600 hover:to-jnj-neon-500 text-white text-sm font-medium rounded-lg shadow-glow-neon hover:shadow-glow-neon-strong transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <i className="bi bi-plus-lg mr-2 text-white"></i>
                New Survey
              </button>
          </div>
        </div>

        {/* Search and Filters */}
        {showFilterFrame && (
          <div className="bg-white rounded-xl border border-[#d9e2f1] shadow-[0_1px_6px_rgba(1,109,247,0.12)] overflow-hidden mb-6">
            <div className="table-head-premium px-5 py-2">
              <h3 className="text-sm font-semibold text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  Filter Options
                </div>
                <button
                  onClick={() => setShowFilterFrame(false)}
                  className="p-1 rounded-md hover:bg-white/20 transition-colors"
                  title="Close Filters"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </h3>
            </div>
            <div className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <select
                  value={searchCategory}
                  onChange={(e) => {
                    setSearchCategory(e.target.value)
                    setPage(1)
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-jnj-neon-500 focus:border-jnj-neon-500 bg-white w-full sm:w-1/2"
                >
                  <option value="all">🔍 All Fields</option>
                  <option value="client">👤 Client</option>
                  <option value="vessel">🚢 Vessel</option>
                  <option value="surveyor">👤 Surveyor</option>
                  <option value="port">⚓ Port</option>
                  <option value="country">🌍 Country</option>
                  <option value="reference">🔢 Reference</option>
                </select>
                <div className="w-full sm:w-1/2 relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
                  <input
                    type="text"
                    placeholder="Search surveys..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      setPage(1)
                    }}
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-jnj-neon-500 focus:border-jnj-neon-500"
                  />
                </div>
                <label className="flex items-center px-3 py-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showCalendarOnly}
                    onChange={(e) => {
                      setShowCalendarOnly(e.target.checked)
                      setPage(1)
                    }}
                    className="w-4 h-4 text-jnj-neon-600 border-gray-300 rounded focus:ring-jnj-neon-500 mr-2"
                  />
                  Calendar Events Only
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Survey Cards */}
        {loading ? (
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-2 border-jnj-neon-500/20 p-16 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-jnj-neon-500/30 border-t-jnj-neon-500"></div>
            <p className="text-gray-700 mt-6 text-base font-medium">Loading surveys...</p>
            <p className="text-gray-500 mt-2 text-sm">Please wait while we fetch your data</p>
          </div>
        ) : surveys.length === 0 ? (
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-2 border-jnj-neon-500/20 p-16 text-center">
            <div className="mb-6">
              <span className="text-6xl text-gray-300">📥</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No surveys found</h3>
            <p className="text-gray-500 text-sm mb-6">Try adjusting your search or filters</p>
            <button
              onClick={() => navigate('/surveys/new')}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-jnj-neon-500 to-jnj-neon-600 hover:from-jnj-neon-600 hover:to-jnj-neon-500 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <i className="bi bi-plus-lg mr-2 text-white"></i>
              Create New Survey
            </button>
          </div>
        ) : (
          <>
            {/* Column Headers - Enhanced (Hidden on mobile) */}
            <div className="table-head-premium hidden md:block rounded-xl px-6 py-2.5 mb-3 shadow-[0_1px_6px_rgba(1,109,247,0.3)]">
              <div className="grid grid-cols-12 gap-3 text-sm font-bold text-gray-700 uppercase tracking-wider">
                <div className="col-span-2 pl-2 flex items-center gap-2">
                  <span className="text-jnj-neon-500">👤</span>
                  <span>Client</span>
                </div>
                <div className="col-span-2 pl-2 flex items-center gap-2">
                  <span className="text-jnj-neon-500">📄</span>
                  <span>Survey Type</span>
                </div>
                <div className="col-span-2 pl-2 flex items-center gap-2">
                  <span className="text-base text-teal-500">🚢</span>
                  <span>Vessel</span>
                </div>
                <div className="col-span-2 pl-2 pr-0 flex items-center gap-2">
                  <span className="text-jnj-neon-500">🌍</span>
                  <span>Country</span>
                </div>
                <div className="col-span-2 pl-0 pr-0 flex items-center gap-2">
                  <span className="text-jnj-neon-500">📍</span>
                  <span>Port</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {surveys.map((survey, index) => {
                const isExpanded = expandedCards.has(survey.reference_number)
                const isLoading = calendarLoading[survey.reference_number]
                const hasOperation = survey.has_operation
                const hasReports = survey.has_reports
                const invoiceSent = survey.invoice_sent === 'Yes'
                const hasReceivedPayment = Boolean(
                  survey.received_payment_date && String(survey.received_payment_date).trim()
                )
                const isCancelled = survey.cancelled
                const lastFourRef = `#${survey.reference_number.slice(-4)}`
                const country = survey.survey_country

                return (
                  <div
                    key={survey.reference_number}
                    className={`group transition-all duration-300 rounded-xl shadow-lg hover:shadow-2xl border-2 overflow-hidden bg-white ${
                      hasReports ? 'border-t-[5px] border-t-[#0a5fe0]' : ''
                    } ${
                      invoiceSent
                        ? 'border-l-[8px] border-l-emerald-500'
                        : 'border-l-[8px] border-l-rose-500'
                    } ${
                      hasReceivedPayment ? 'border-r-[8px] border-r-amber-400' : ''
                    } ${isCancelled ? 'opacity-70 grayscale-[0.3]' : ''} ${isExpanded ? 'ring-2 ring-jnj-neon-500/50 shadow-2xl' : ''}`}
                  >
                    {/* Card Header - Enhanced */}
                    <div
                      className={`px-3 md:pl-5 md:pr-6 py-1 md:py-0.5 cursor-pointer transition-all duration-200 border-b border-gray-200/50 hover:bg-gray-50`}
                      onClick={(e) => toggleCard(survey.reference_number, e)}
                    >
                      {/* Mobile Layout */}
                      <div className="md:hidden space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className={`text-sm ${isCancelled ? 'text-pink-500' : 'text-jnj-neon-500'}`}>👤</span>
                            <span className={`truncate font-semibold text-base ${isCancelled ? 'text-pink-400' : 'text-gray-900'}`} title={survey.client_name}>{survey.client_name}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0 -mr-4">
                            <img 
                              src={`${process.env.PUBLIC_URL || ''}/images/logo_shape.png`}
                              alt="JNJ Logo" 
                              className={`w-7 h-7 object-contain ${isCancelled ? 'opacity-50' : 'opacity-100'}`}
                            />
                            <span className={`text-xs font-bold ${isCancelled ? 'text-pink-400' : 'text-jnj-neon-600'}`}>{lastFourRef}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base ${isCancelled ? 'text-pink-400' : 'text-gray-600'}`}>📄</span>
                            <span className={`truncate ${isCancelled ? 'text-pink-400' : 'text-gray-700'}`} title={survey.survey_type}>{survey.survey_type}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base ${isCancelled ? 'text-pink-400' : 'text-teal-500'}`}>🚢</span>
                            <span className={`truncate ${isCancelled ? 'text-pink-400' : 'text-gray-700'}`} title={survey.vessel_name}>{survey.vessel_name}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base ${isCancelled ? 'text-pink-400' : 'text-gray-600'}`}>🌍</span>
                            <span className={`truncate ${isCancelled ? 'text-pink-400' : 'text-gray-700'}`} title={country}>{country}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base ${isCancelled ? 'text-pink-400' : 'text-gray-600'}`}>📍</span>
                            <span className={`truncate ${isCancelled ? 'text-pink-400' : 'text-gray-700'}`} title={survey.survey_port}>{survey.survey_port}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-1">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/surveys/${survey.reference_number}/edit`)
                              }}
                              className="p-1.5 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95"
                              title="Edit Survey"
                            >
                              <span className="text-2xl">✏️</span>
                            </button>
                            {/* Invoice Button (Superuser only) */}
                            {hasFullAccess && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/surveys/${survey.reference_number}/invoice`)
                                }}
                                className="p-1.5 rounded-lg transition-all duration-200 flex-shrink-0 hover:scale-110 active:scale-95"
                                title="Invoice"
                              >
                                <span className="text-3xl">🧾</span>
                              </button>
                            )}
                            <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={(e) => handleRemoveFromCalendar(survey.reference_number, e)}
                                disabled={isLoading || !hasOperation}
                                className={`p-1.5 rounded-lg transition-all duration-200 ${
                                  hasOperation
                                    ? 'bg-pink-100 hover:bg-pink-200 text-pink-700'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title="Remove from Calendar"
                              >
                                {isLoading && hasOperation ? (
                                  <i className="bi bi-arrow-clockwise animate-spin text-base"></i>
                                ) : (
                                  <i className="bi bi-calendar-minus-fill text-xl"></i>
                                )}
                              </button>
                              <button
                                onClick={(e) => handleAddToCalendar(survey.reference_number, e)}
                                disabled={isLoading || hasOperation}
                                className={`p-1.5 rounded-lg transition-all duration-200 ${
                                  !hasOperation
                                    ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title="Add to Calendar"
                              >
                                {isLoading && !hasOperation ? (
                                  <i className="bi bi-arrow-clockwise animate-spin text-base"></i>
                                ) : (
                                  <i className="bi bi-calendar-plus-fill text-xl"></i>
                                )}
                              </button>
                            </div>
                          </div>
                          <div className={`text-sm px-2 py-1 rounded-full font-medium ${
                            invoiceSent 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-rose-100 text-rose-700'
                          }`}>
                            Invoice {invoiceSent ? 'Sent' : 'Pending'}
                          </div>
                        </div>
                      </div>
                      {/* Desktop Layout */}
                      <div className="hidden md:grid grid-cols-12 gap-3 items-center text-base">
                        <div className="col-span-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-sm ${isCancelled ? 'text-pink-500' : 'text-jnj-neon-500'}`}>👤</span>
                            <span className={`truncate whitespace-nowrap text-base ${isCancelled ? 'text-pink-400' : 'text-gray-900'}`} title={survey.client_name}>{survey.client_name}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-sm ${isCancelled ? 'text-pink-500' : 'text-jnj-neon-500'}`}>📄</span>
                            <span className={`truncate whitespace-nowrap text-base ${isCancelled ? 'text-pink-400' : 'text-gray-900'}`} title={survey.survey_type}>{survey.survey_type}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base ${isCancelled ? 'text-pink-400' : 'text-teal-500'}`}>🚢</span>
                            <span className={`truncate whitespace-nowrap text-base ${isCancelled ? 'text-pink-400' : 'text-gray-900'}`} title={survey.vessel_name}>{survey.vessel_name}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-2 pr-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-sm ${isCancelled ? 'text-pink-500' : 'text-jnj-neon-500'}`}>🌍</span>
                            <span className={`truncate whitespace-nowrap text-base ${isCancelled ? 'text-pink-400' : 'text-gray-900'}`} title={country}>{country}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-0 pr-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-sm ${isCancelled ? 'text-pink-500' : 'text-jnj-neon-500'}`}>📍</span>
                            <span className={`truncate whitespace-nowrap text-base ${isCancelled ? 'text-pink-400' : 'text-gray-900'}`} title={survey.survey_port}>{survey.survey_port}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-0 pr-0 min-w-[100px] flex justify-end items-center gap-6 -mr-2">
                          <div className="flex items-center gap-1 -ml-4">
                            <span className={`truncate whitespace-nowrap text-sm font-bold ${isCancelled ? 'text-pink-400' : 'text-jnj-neon-600'}`}>{lastFourRef}</span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/surveys/${survey.reference_number}/edit`)
                              }}
                              className="p-2 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95"
                              title="Edit Survey"
                            >
                              <span className="text-2xl">✏️</span>
                            </button>
                            {/* Invoice Button (Superuser only) */}
                            {hasFullAccess && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/surveys/${survey.reference_number}/invoice`)
                                }}
                                className="p-2 rounded-lg transition-all duration-200 flex-shrink-0 hover:scale-110 active:scale-95"
                                title="Invoice"
                              >
                                <span className="text-3xl">🧾</span>
                              </button>
                            )}
                            <button
                              onClick={(e) => handleRemoveFromCalendar(survey.reference_number, e)}
                              disabled={isLoading || !hasOperation}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                hasOperation
                                  ? 'bg-pink-100 hover:bg-pink-200 text-pink-700 hover:text-pink-800 hover:scale-110 active:scale-95'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="Remove from Calendar"
                            >
                              {isLoading && hasOperation ? (
                                <i className="bi bi-arrow-clockwise animate-spin text-lg"></i>
                              ) : (
                                <i className="bi bi-calendar-minus-fill text-xl"></i>
                              )}
                            </button>
                            <button
                              onClick={(e) => handleAddToCalendar(survey.reference_number, e)}
                              disabled={isLoading || hasOperation}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                !hasOperation
                                  ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700 hover:text-emerald-800 hover:scale-110 active:scale-95'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="Add to Calendar"
                            >
                              {isLoading && !hasOperation ? (
                                <i className="bi bi-arrow-clockwise animate-spin text-lg"></i>
                              ) : (
                                <i className="bi bi-calendar-plus-fill text-xl"></i>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Body - Expandable */}
                    {isExpanded && (
                      <>
                        <div 
                          className="px-3 md:px-6 py-4 md:py-5 max-h-96 overflow-y-auto border-b border-gray-100"
                          onClick={(e) => e.stopPropagation()}
                          onScroll={(e) => e.stopPropagation()}
                        >
                          {/* Column-based Layout */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {/* 1. Basic Information Section */}
                            <div className="bg-gradient-to-br from-jnj-neon-500/10 to-jnj-neon-500/5 rounded-xl p-3 border-2 border-jnj-neon-500/30 shadow-md">
                            <h4 className="text-sm text-jnj-neon-700 uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold">
                              <i className="bi bi-info-circle text-jnj-neon-600 text-base"></i>
                              Basic Information
                            </h4>
                              <div className="space-y-2">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Reference Number</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.reference_number || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Client Name</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.client_name || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Client Reference</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.client_ref_no || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Operator</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.operator || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Survey Type</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.survey_type || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold flex items-center gap-2">
                                    <span className="text-base text-teal-500">🚢</span>
                                    Vessel Name
                                  </span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.vessel_name || '-'}</span>
                                </div>
                              </div>
                            </div>

                            {/* 2. Location & Schedule & Notes Section */}
                            <div className="bg-gradient-to-br from-jnj-neon-500/10 to-jnj-neon-500/5 rounded-xl p-3 border-2 border-jnj-neon-500/30 shadow-md">
                            <h4 className="text-sm text-jnj-neon-700 uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold">
                              <i className="bi bi-geo-alt text-jnj-neon-600 text-base"></i>
                              Location & Schedule & Notes
                            </h4>
                              <div className="space-y-2">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Country</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.survey_country || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Port</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.survey_port || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Notes</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.notes || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">ETA</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{formatDate(survey.eta)}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Appointment Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{formatDate(survey.appointment_date)}</span>
                                </div>
                              </div>
                            </div>

                            {/* 3. Surveyor Details Section */}
                            <div className="bg-gradient-to-br from-jnj-neon-500/10 to-jnj-neon-500/5 rounded-xl p-3 border-2 border-jnj-neon-500/30 shadow-md">
                            <h4 className="text-sm text-jnj-neon-700 uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold">
                              <i className="bi bi-person text-jnj-neon-600 text-base"></i>
                              Surveyor Details
                            </h4>
                              <div className="space-y-2">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Surveyor Type</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.surveyor_type || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Surveyor</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.surveyor_code || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Subcon</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{hasFullAccess ? (survey.subcon || '-') : '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Subcon Fee Paid</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">
                                    {hasFullAccess ? (
                                      survey.subcon_fee_paid ? (
                                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">Yes</span>
                                      ) : (
                                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">No</span>
                                      )
                                    ) : (
                                      '-'
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* 4. Operation Dates Section */}
                            <div className="bg-gradient-to-br from-jnj-neon-500/10 to-jnj-neon-500/5 rounded-xl p-3 border-2 border-jnj-neon-500/30 shadow-md">
                            <h4 className="text-sm text-jnj-neon-700 uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold">
                              <i className="bi bi-calendar text-jnj-neon-600 text-base"></i>
                              Operation Dates
                            </h4>
                              <div className="space-y-2">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Attendance Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{formatDate(survey.attendance_date)}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Due Date of Payment</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{formatDate(survey.due_date_payment)}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Received Payment Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{hasFullAccess ? formatDate(survey.received_payment_date) : '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Initial Report Sent Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{formatDate(survey.initial_report_sent_date)}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Final Report Sent Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{formatDate(survey.final_report_sent_date)}</span>
                                </div>
                              </div>
                            </div>

                            {/* 5. Bunker Information Section - Full Width */}
                            <div className="col-span-full bg-gradient-to-br from-jnj-neon-500/10 to-jnj-neon-500/5 rounded-xl p-3 border-2 border-jnj-neon-500/30 shadow-md">
                            <h4 className="text-sm text-jnj-neon-700 uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold">
                              <i className="bi bi-fuel-pump text-jnj-neon-600 text-base"></i>
                              Bunker Information
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* LSFO */}
                              <div className="bg-white rounded-xl p-4 border-2 shadow-lg hover:shadow-xl transition-all" style={{ borderColor: '#0a5fe0' }}>
                                <h5 className="text-sm mb-4 pb-3 border-b-2 font-bold uppercase tracking-wide" style={{ color: '#0a5fe0', borderColor: '#0a5fe0' }}>LSFO Details</h5>
                                <div className="space-y-2.5 text-sm">
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Logbook</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.lsfo_logbook)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Survey</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.lsfo_survey)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center pt-2.5 border-t-2 border-gray-200 py-2 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                    <span className="text-gray-700 font-semibold">Difference</span>
                                    <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatFloat(survey.lsfo_qty)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">BDN Supp. Fig.</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.lsfo_bdn)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Vessel Supp. Fig.</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.lsfo_vessel)} MT</span>
                                  </div>
                                  {survey.lsfo_barge_name && (
                                    <div className="flex justify-between items-center py-2 px-2 rounded-lg border" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)', borderColor: 'rgba(10, 95, 224, 0.3)' }}>
                                      <span className="text-gray-600 font-medium">Barge Name</span>
                                      <span className="font-semibold" style={{ color: '#0a5fe0' }}>{survey.lsfo_barge_name}</span>
                                    </div>
                                  )}
                                  {hasFullAccess && (
                                    <>
                                      <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300 py-2.5 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                        <span className="text-gray-700 font-semibold">Price</span>
                                        <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatCurrency(survey.lsfo_price)}</span>
                                      </div>
                                      <div className="flex justify-between items-center py-2.5 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                        <span className="text-gray-700 font-semibold">Incentive</span>
                                        <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatCurrency(survey.lsfo_recovery)}</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* HSFO */}
                              <div className="bg-white rounded-xl p-4 border-2 shadow-lg hover:shadow-xl transition-all" style={{ borderColor: '#0a5fe0' }}>
                                <h5 className="text-sm mb-4 pb-3 border-b-2 font-bold uppercase tracking-wide" style={{ color: '#0a5fe0', borderColor: '#0a5fe0' }}>HSFO Details</h5>
                                <div className="space-y-2.5 text-sm">
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Logbook</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.hsfo_logbook)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Survey</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.hsfo_survey)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center pt-2.5 border-t-2 border-gray-200 py-2 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                    <span className="text-gray-700 font-semibold">Difference</span>
                                    <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatFloat(survey.hsfo_qty)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">BDN Supp. Fig.</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.hsfo_bdn)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Vessel Supp. Fig.</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.hsfo_vessel)} MT</span>
                                  </div>
                                  {survey.hsfo_barge_name && (
                                    <div className="flex justify-between items-center py-2 px-2 rounded-lg border" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)', borderColor: 'rgba(10, 95, 224, 0.3)' }}>
                                      <span className="text-gray-600 font-medium">Barge Name</span>
                                      <span className="font-semibold" style={{ color: '#0a5fe0' }}>{survey.hsfo_barge_name}</span>
                                    </div>
                                  )}
                                  {hasFullAccess && (
                                    <>
                                      <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300 py-2.5 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                        <span className="text-gray-700 font-semibold">Price</span>
                                        <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatCurrency(survey.hsfo_price)}</span>
                                      </div>
                                      <div className="flex justify-between items-center py-2.5 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                        <span className="text-gray-700 font-semibold">Incentive</span>
                                        <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatCurrency(survey.hsfo_recovery)}</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* MGO */}
                              <div className="bg-white rounded-xl p-4 border-2 shadow-lg hover:shadow-xl transition-all" style={{ borderColor: '#0a5fe0' }}>
                                <h5 className="text-sm mb-4 pb-3 border-b-2 font-bold uppercase tracking-wide" style={{ color: '#0a5fe0', borderColor: '#0a5fe0' }}>MGO Details</h5>
                                <div className="space-y-2.5 text-sm">
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Logbook</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.mgo_logbook)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Survey</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.mgo_survey)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center pt-2.5 border-t-2 border-gray-200 py-2 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                    <span className="text-gray-700 font-semibold">Difference</span>
                                    <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatFloat(survey.mgo_qty)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">BDN Supp. Fig.</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.mgo_bdn)} MT</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 font-medium">Vessel Supp. Fig.</span>
                                    <span className="text-gray-900 font-semibold">{formatFloat(survey.mgo_vessel)} MT</span>
                                  </div>
                                  {survey.mgo_barge_name && (
                                    <div className="flex justify-between items-center py-2 px-2 rounded-lg border" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)', borderColor: 'rgba(10, 95, 224, 0.3)' }}>
                                      <span className="text-gray-600 font-medium">Barge Name</span>
                                      <span className="font-semibold" style={{ color: '#0a5fe0' }}>{survey.mgo_barge_name}</span>
                                    </div>
                                  )}
                                  {hasFullAccess && (
                                    <>
                                      <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300 py-2.5 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                        <span className="text-gray-700 font-semibold">Price</span>
                                        <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatCurrency(survey.mgo_price)}</span>
                                      </div>
                                      <div className="flex justify-between items-center py-2.5 px-2 rounded-lg" style={{ backgroundColor: 'rgba(10, 95, 224, 0.1)' }}>
                                        <span className="text-gray-700 font-semibold">Incentive</span>
                                        <span className="font-bold" style={{ color: '#0a5fe0' }}>{formatCurrency(survey.mgo_recovery)}</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 6. Financial Details Section (Superuser only) - Full Width */}
                          {hasFullAccess && (
                            <div className="col-span-full bg-gradient-to-br from-jnj-neon-500/10 to-jnj-neon-500/5 rounded-xl p-3 border-2 border-jnj-neon-500/30 shadow-md">
                              <h4 className="text-sm text-jnj-neon-700 uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold">
                                <i className="bi bi-currency-dollar text-jnj-neon-600 text-base"></i>
                                Financial Details
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">PCT Incentive</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.pct_qty !== null ? `${formatFloat(survey.pct_qty, 2)}%` : '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">MT Price</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.mt_price !== null ? formatCurrency(survey.mt_price) : '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Incentive</span>
                                  <span className="text-gray-900 text-sm font-semibold">{formatCurrency(survey.recovery)}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Survey Fee</span>
                                  <span className="text-gray-900 text-sm font-semibold">{formatCurrency(survey.survey_fee)}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Expenses</span>
                                  <span className="text-gray-900 text-sm font-semibold">{formatCurrency(survey.expenses)}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Discount</span>
                                  <span className="text-gray-900 text-sm font-semibold">{formatCurrency(survey.discount)}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Others</span>
                                  <span className="text-gray-900 text-sm font-semibold">{formatCurrency(survey.others)}</span>
                                </div>
                                <div className="rounded-lg p-2 border-2 shadow-md" style={{ background: 'linear-gradient(to bottom right, rgba(10, 95, 224, 0.05), rgba(10, 95, 224, 0.08))', borderColor: '#0a5fe0' }}>
                                  <span className="block mb-0.5 text-sm font-bold" style={{ color: '#0a5fe0' }}>Total</span>
                                  <span className="text-sm font-bold" style={{ color: '#0029a4' }}>{formatCurrency(survey.total)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          </div>
                        </div>

                        {/* Fixed Bottom Actions */}
                        <div className="px-3 md:px-6 py-3 md:py-4 bg-white border-t border-gray-300 rounded-b-lg">
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
                            {/* Cancel/Activate Button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault()
                                handleToggleStatus(survey.reference_number, e)
                              }}
                              className={`px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-2 ${
                                survey.cancelled
                                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm hover:shadow-md'
                                  : 'bg-pink-200 hover:bg-pink-300 text-pink-800 shadow-sm hover:shadow-md'
                              }`}
                            >
                              <i className={`bi ${survey.cancelled ? 'bi-check-circle' : 'bi-x-circle'}`}></i>
                              <span>{survey.cancelled ? 'Activate' : 'Survey Cancel'}</span>
                            </button>

                            {/* Right Side Actions */}
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                              {/* Upload/Download Report */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault()
                                  setReportsModalRef(survey.reference_number)
                                }}
                                className="px-4 py-2.5 text-sm font-semibold rounded-md bg-premium text-white shadow-[0_2px_8px_rgba(1,109,247,0.3)] hover:shadow-[0_4px_14px_rgba(1,109,247,0.4)] transition-shadow flex items-center justify-center gap-2"
                              >
                                <i className="bi bi-folder2-open"></i>
                                {isManager ? 'Upload/Download Report' : 'View Reports'}
                              </button>

                              {/* Invoice Sent Toggle */}
                              <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 rounded-md border border-gray-300">
                                <span className="text-xs font-medium text-gray-700">
                                  Invoice Sent:
                                </span>
                                <div className="flex items-center gap-1 bg-white rounded border border-gray-300 p-0.5">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleToggleInvoiceSent(survey.reference_number, false, e)
                                    }}
                                    className={`px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                                      !invoiceSent
                                        ? 'bg-rose-500 text-white'
                                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                                    }`}
                                  >
                                    No
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleToggleInvoiceSent(survey.reference_number, true, e)
                                    }}
                                    className={`px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                                      invoiceSent
                                        ? 'bg-emerald-500 text-white'
                                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                                    }`}
                                  >
                                    Yes
                                  </button>
                                </div>
                              </div>

                              {/* Invoice Button (Superuser only) - REMOVED */}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-6">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="bi bi-chevron-left mr-1"></i>
                  Previous
                </button>
                <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <i className="bi bi-chevron-right ml-1"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {reportsModalRef && (
        <SurveyReportsModal
          referenceNumber={reportsModalRef}
          canUpload={isManager}
          onClose={() => setReportsModalRef(null)}
          onChanged={fetchSurveys}
        />
      )}
    </div>
  )
}

export default SurveyList
